import { TrackProperties } from "./types";

const STORAGE_KEY = "flexspace.customerio_pending_checkout_started";

// A cart left for longer than this is too stale to remind anyone about. It
// matches how far back Customer.io's own merge of anonymous activity can still
// start an automation.
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

interface PendingEntry {
  properties: TrackProperties;
  savedAt: number;
}

/**
 * The latest checkout of a visitor Customer.io can't name yet, waiting for
 * their email. The browser's copy is the one that counts, so every tab sees the
 * same checkout, and sending or discarding it in one tab ends it in all of
 * them. A page keeps its own copy only when the browser refuses to store it.
 */
export function createPendingCheckoutStarted() {
  let pageCopy: PendingEntry | undefined;

  function discard() {
    pageCopy = undefined;
    safeRemoveItem(STORAGE_KEY);
  }

  return {
    save(properties: TrackProperties) {
      const entry = { properties, savedAt: Date.now() };
      const stored = safeSetItem(STORAGE_KEY, JSON.stringify(entry));
      pageCopy = stored ? undefined : entry;
    },
    /** The waiting checkout unless it is too old; either way it stops waiting. */
    take(): TrackProperties | undefined {
      const entry = pageCopy ?? safeParse(safeGetItem(STORAGE_KEY));
      discard();
      if (!entry || Date.now() - entry.savedAt > MAX_AGE_MS) return undefined;

      return entry.properties;
    },
    discard,
  };
}

function safeParse(raw: string | null): PendingEntry | undefined {
  if (!raw) return undefined;

  try {
    const value: Partial<PendingEntry> = JSON.parse(raw);
    if (typeof value.savedAt !== "number" || !value.properties) {
      return undefined;
    }

    return { properties: value.properties, savedAt: value.savedAt };
  } catch {
    return undefined;
  }
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    // Private mode or a full quota: the page keeps the checkout itself.
    return false;
  }
}

function safeRemoveItem(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Private mode: nothing was stored to remove.
  }
}
